/*eslint-disable max-lines*/
import type {CoordinateFormat, UnitSystem} from '@gaia/types';
import {toUTM} from '@gaia/utils/gaia/getSyncMapboxStyle/getGridMapboxStyle/UTM';

export const metersToUsersUnits = (meters: number, unitSystem: UnitSystem) => {
    if (unitSystem === 'metric') {
        return meters;
    }
    return meters * 3.281;
};

/**
 * Converts distance in meters to kilometers or miles based on the user's preferred unit system
 * @param meters
 * @param unitSystem
 * @returns {number} the distance in km or mi
 */
export const metersToUsersDistanceUnits = (meters: number, unitSystem: UnitSystem) => {
    if (unitSystem === 'metric') {
        return meters / 1000.0;
    }
    return (meters * 3.281) / 5280;
};

export function formatDistance(meters: number, unitSystem: UnitSystem, formatted?: false): string;
export function formatDistance(
    meters: number,
    unitSystem: UnitSystem,
    formatted: true
): {val: string; label: 'm' | 'ft' | 'km' | 'mi'};
export function formatDistance(
    meters: number,
    unitSystem: UnitSystem,
    formatted = false
): {val: string; label: 'm' | 'ft' | 'km' | 'mi'} | string {
    let convertedMeters = meters;
    if (isNaN(convertedMeters)) {
        convertedMeters = 0;
    }
    let decimalPlaces = 2;
    if (unitSystem === 'metric') {
        if (meters < 500) {
            if (formatted) {
                return {val: convertedMeters.toFixed(0), label: 'm'};
            }
            return `${convertedMeters.toFixed(0)} m`;
        }
        const km = convertedMeters / 1000.0;
        if (km > 10) {
            decimalPlaces = 1;
        }
        if (km > 1000) {
            decimalPlaces = 0;
        }
        // add , to numbers longer than 3 characters
        // https://stackoverflow.com/a/62256954
        if (formatted) {
            return {
                val: km.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                label: 'km',
            };
        }
        return `${km.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} km`;
    }

    const feet = convertedMeters * 3.281;
    if (feet < 500) {
        if (formatted) {
            return {val: feet.toFixed(0), label: 'ft'};
        }
        return `${feet.toFixed(0)} ft`;
    }
    const miles = feet / 5280;
    if (miles > 10) {
        decimalPlaces = 1;
    }
    if (miles > 1000) {
        decimalPlaces = 0;
    }
    if (formatted) {
        return {
            val: miles.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
            label: 'mi',
        };
    }
    return `${miles.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} mi`;
}

export const formatArea = (meters: number, unitSystem: UnitSystem) => {
    let convertedMeters = meters;
    if (isNaN(convertedMeters)) {
        convertedMeters = 0;
    }
    let decimalPlaces = 2;

    if (unitSystem === 'metric') {
        if (meters < 1000000) {
            return `${convertedMeters.toFixed(0)} m²`;
        }
        const km = convertedMeters / 1000000.0;
        if (km > 10) {
            decimalPlaces = 1;
        }
        if (km > 100) {
            decimalPlaces = 0;
        }
        return `${km.toFixed(decimalPlaces)} km²`;
    }

    const acres = convertedMeters * 0.000247105;
    if (acres > 10) {
        decimalPlaces = 1;
    }
    if (acres > 1000) {
        decimalPlaces = 0;
    }
    if (acres > 1000000) {
        return `${(acres / 1000000).toFixed(decimalPlaces)} million acres`;
    } else if (acres > 10000) {
        return `${(acres / 1000).toFixed(decimalPlaces)}k acres`;
    }
    return `${acres.toFixed(decimalPlaces)} acres`;
};

export function formatElevation(meters: number, unitSystem: UnitSystem, formatted?: false): string;
export function formatElevation(
    meters: number,
    unitSystem: UnitSystem,
    formatted: true
): {val: string; label: 'm' | 'ft'};
export function formatElevation(
    meters: number,
    unitSystem: UnitSystem,
    formatted = false
): {val: string; label: 'm' | 'ft'} | string {
    let convertedMeters = meters;
    if (!meters || isNaN(meters)) {
        convertedMeters = 0;
    }

    if (unitSystem === 'metric') {
        const elevation = Math.round(convertedMeters).toLocaleString();
        if (formatted) return {val: elevation, label: 'm'};
        return `${elevation} m`;
    }
    const elevation = Math.round(convertedMeters * 3.281).toLocaleString();
    if (formatted) return {val: elevation, label: 'ft'};
    return `${elevation} ft`;
}

// Format a given elevation value without converting or rounding it.
// This is useful when the unit is already known, e.g. map items which have an ele_feet property.
export function formatRawElevation(elevation: number, unitSystem: UnitSystem) {
    const localizedElevation = elevation.toLocaleString();
    if (unitSystem === 'metric') return `${localizedElevation} m`;
    return `${localizedElevation} ft`;
}

export const formatTimeWithDays = (seconds: number, longForm?: boolean) => {
    let hours = seconds / 3600;
    const days = Math.floor(hours / 24);
    const minutes = Math.floor((hours - Math.floor(hours)) * 60);
    hours = Math.floor(hours - days * 24);
    let timeString;
    if (longForm) {
        const hrs = `${hours} ${hours > 1 || hours === 0 ? 'Hours' : 'Hour'}`;
        const mins = ` ${minutes} ${minutes > 1 || minutes === 0 ? 'Minutes' : 'Minute'}`;
        timeString = `${hrs} ${mins}`;
    } else {
        timeString = `${hours}h ${minutes}m`;
    }
    if (days > 0) {
        if (longForm) {
            const dUnit = days > 1 ? 'Days' : 'Day';
            return `${days} ${dUnit} ${timeString}`;
        } else {
            return `${days}d ${timeString}`;
        }
    }
    return timeString;
};

export const formatTimeWithHours = (seconds: number) => {
    const hours = (seconds / 3600).toFixed(0);
    const minutes = Math.round(seconds / 60) % 60;
    let minutesString = minutes.toFixed(0);
    minutesString = `${minutesString} min`;
    if (hours === '0') {
        return minutesString;
    }
    return `${hours} hrs ${minutesString}`;
};

export const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds / 60) - hours * 60;
    const leftover = Math.floor(seconds % 60);

    if (hours) return `${hours}h ${minutes}m`;
    else if (minutes || leftover) return `${minutes}m ${leftover}s`;
    return '-';
};

export const formatRouteType = (routeType: 'oneway' | 'loop' | 'outandback') => {
    if (routeType === 'oneway') return 'One Way';
    else if (routeType === 'loop') return 'Loop';
    else if (routeType === 'outandback') return 'Out & Back';
};

export const formatPace = (seconds: number, distance: number, unitSystem: UnitSystem) => {
    const multiplier = unitSystem === 'metric' ? 1000.0 : 1609.344;
    const units = unitSystem === 'metric' ? 'km' : 'mi';

    const formattedDistance = distance / multiplier;
    if (!formattedDistance || !seconds) return '-';

    const minutes = seconds / 60;
    const minuteUnits = minutes / formattedDistance;

    const formattedSeconds = Math.floor((minuteUnits % 1) * 60)
        .toString()
        .padStart(2, '0');
    return `${Math.floor(minuteUnits)}:${formattedSeconds} min/${units}`;
};

export const formatSpeed = (metersPerSecond: number, unitSystem: UnitSystem) => {
    metersPerSecond = isNaN(metersPerSecond) || metersPerSecond === Infinity ? 0 : metersPerSecond;
    if (metersPerSecond === 0) return '-';

    // One decimal place if ~ < 25mph
    const decimalPlaces = metersPerSecond > 11 ? 0 : 1;

    if (unitSystem === 'metric') {
        const kph = metersPerSecond * 3.6;
        return `${kph.toFixed(decimalPlaces)} kph`;
    }

    const mph = metersPerSecond * 2.23;
    return `${mph.toFixed(decimalPlaces)} mph`;
};

const MINUTES = 60;
const SECONDS = 3600;

const getCardinalDirection = function (coordinates: number[]) {
    const lng = coordinates[0] < 0 ? 'W' : 'E';
    const lat = coordinates[1] < 0 ? 'S' : 'N';
    return [lng, lat];
};

/**
 * Given an array of decimal coordinates in the format [lng, lat],
 * return a string formatted as Degrees, Minutes, Seconds (DMS).
 */
export const formatCoordinatesDMS = (coordinates: number[], precision = 2) => {
    function toDMS(coordinate: number) {
        const absoluteDegree = Math.abs(coordinate);
        let degree = Math.floor(absoluteDegree);
        let minutes = Math.floor((absoluteDegree - degree) * MINUTES);
        let seconds = Number(
            ((absoluteDegree - degree - minutes / MINUTES) * SECONDS).toFixed(precision)
        );
        if (seconds === MINUTES) {
            seconds = 0;
            minutes = minutes + 1;
        }
        if (minutes === MINUTES) {
            minutes = 0;
            degree = degree + 1;
        }
        return `${degree}° ${minutes}' ${seconds.toFixed(precision)}"`;
    }
    const cardinal = getCardinalDirection(coordinates);
    return `${toDMS(coordinates[1])} ${cardinal[1]}, ${toDMS(coordinates[0])} ${cardinal[0]}`;
};

/**
 * Given an array of decimal coordinates in the format [lng, lat],
 * return a string formatted as Degrees, Decimal Minutes (DDM).
 */
export const formatCoordinatesDDM = (coordinates: number[], precision = 4) => {
    function toDDM(coordinate: number) {
        const absoluteDegree = Math.abs(coordinate);
        let degree = Math.floor(absoluteDegree);
        let decimalMinutes = Number(((absoluteDegree - degree) * MINUTES).toFixed(precision));
        if (decimalMinutes === MINUTES) {
            decimalMinutes = 0;
            degree = degree + 1;
        }
        return `${degree}° ${decimalMinutes.toFixed(precision)}'`;
    }
    const cardinal = getCardinalDirection(coordinates);
    return `${toDDM(coordinates[1])} ${cardinal[1]}, ${toDDM(coordinates[0])} ${cardinal[0]}`;
};

const formatCoordinatesUTM = (coordinates: number[]) => {
    const {zoneNumber, zoneLetter, easting, northing} = toUTM(coordinates);
    return `${zoneNumber}${zoneLetter} ${easting}E ${northing}N`;
};

export const formatCoordinates = (coordinates: number[], precision = 5) => {
    return `${coordinates[1].toFixed(precision)}, ${coordinates[0].toFixed(precision)}`;
};

export const coordinatesToUsersFormat = (
    coordinates: number[],
    coordinateFormat: CoordinateFormat
) => {
    switch (coordinateFormat) {
        case 'dd':
            return formatCoordinates(coordinates);
        case 'ddm':
            return formatCoordinatesDDM(coordinates);
        case 'dms':
            return formatCoordinatesDMS(coordinates);
        case 'utm':
            return formatCoordinatesUTM(coordinates);
    }
};

const monthLetterMap = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
export const formatMonth = (monthIndex: number) => {
    return monthLetterMap[monthIndex];
};

const monthAbbrevMap = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

export const formatMonthAbbrev = (monthIndex: number) => {
    return monthAbbrevMap[monthIndex];
};

export function getShortFilename(filename: string | undefined, size = 15) {
    if (!filename) return '';
    if (filename.length <= size) return filename;
    const parts = filename.split('.');
    const extension = parts.pop();
    const extensionLength = extension?.length || 0;
    const base = parts.join('.');

    return base.substr(0, size - extensionLength - 1) + '…' + (extension ?? '');
}

export function formatDate(date: string | undefined, options?: {includeTime?: boolean}) {
    const includeTime = options?.includeTime ?? true;
    // January 1, 2020, 12:00 AM
    return date
        ? new Date(date).toLocaleString([], {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              ...(includeTime
                  ? {
                        hour: '2-digit',
                        minute: '2-digit',
                    }
                  : undefined),
          })
        : '';
}

export function toTitleCase(word: string) {
    return word.charAt(0).toUpperCase() + word.slice(1);
}

export function shortenString(s: string | undefined, size = 15) {
    if (!s) return '';
    if (s.length <= size) return s;

    return s.substr(0, size).trim() + '…';
}

export function formatUnitString(abbrev: 'mi' | 'ft' | 'km' | 'm', plural?: boolean) {
    // Turn abbreviated unity string to long form
    const abbrevMap = {
        mi: 'Mile',
        ft: 'Feet',
        km: 'Kilometer',
        m: 'Meter',
    };
    if (plural && abbrev !== 'ft') {
        return abbrevMap[abbrev] + 's';
    } else {
        return abbrevMap[abbrev];
    }
}

export const formatTemperature = (
    degreesCelsius: number,
    unitSystem: UnitSystem,
    includeDegree?: boolean
) => {
    const degreesFahrenheit = (degreesCelsius * 9) / 5 + 32;
    const degreesCelsiusRounded = degreesCelsius.toFixed(0);
    const degreesFahrenheitRounded =
        degreesFahrenheit.toFixed(0) === '-0' // remove negative symbol if the value rounds to zero
            ? '0'
            : degreesFahrenheit.toFixed(0);

    const temperature = unitSystem === 'metric' ? degreesCelsiusRounded : degreesFahrenheitRounded;
    const unit = unitSystem === 'metric' ? 'C' : 'F';
    const displayDegree = includeDegree ? '°' : '';

    return {
        formattedTemperature: `${temperature}${displayDegree}`,
        unit,
    };
};

export const formatWindSpeed = (kilometersPerHour: number, unitSystem: UnitSystem) => {
    if (unitSystem === 'metric') {
        return {formattedWindSpeed: kilometersPerHour.toFixed(0), unit: 'km/h'};
    }
    return {
        formattedWindSpeed: kilometersPerHourToMilesPerHour(kilometersPerHour).toFixed(0),
        unit: 'mph',
    };
};

export const kilometersPerHourToMilesPerHour = (kilometersPerHour: number) => {
    return kilometersPerHour / 1.609344;
};

export const centimetersToInches = (centimeters: number) => {
    return centimeters / 2.54;
};
